import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, IconButton, TextField, Typography } from "@mui/material";
import DataTable from "../../common/DataTable";
import { api } from "../../api/api";
import { productInventoryEndPoints } from "../../constants/endpoints";
import editIcon from "../../assets/edit-icon.svg";
import Toast from "../../common/Toast";
import { SxProps } from "@mui/material";

interface Inventory {
  id: number;
  productName: string;
  productId: string;
  inventoryId: number;
  quantity_20L: number;
  quantity_30L: number;
  quantity_50L: number;
  editIcon: string;
}

type BulkUpdatePayload = {
  quantity_20L?: number;
  quantity_30L?: number;
  quantity_50L?: number;
  productId: string;
  inventoryId: number;
};
interface Column {
  label: string;
  field: string;
  align?: "left" | "right" | "center";
  renderCell?: (row: any) => React.ReactNode;
  labelStyle?: SxProps;
  cellStyle?: React.CSSProperties;
}

const InventoryDetails = () => {
  const [inventoryDataState, setInventoryDataState] = useState<Inventory[]>([]);
  const [bulkUpdatePayload, setBulkUpdatePayload] = useState<
    BulkUpdatePayload[]
  >([]);
  const [isEditing, setIsEditing] = useState<number | null>(null);
  const [tempQuantities, setTempQuantities] = useState<
    Record<number, Partial<Inventory>>
  >({});
  const [error, setError] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    const loadInventoryData = async () => {
      setError(false);
      try {
        const response = await api.get(
          `${productInventoryEndPoints.productInventory}`
        );
        setInventoryDataState(response?.data?.data?.data);
      } catch (error) {
        console.error("Error fetching Inventory Items", error);
        setError(true);
        setToastMessage("Failed to fetch Inventory Items");
        setShowToast(true);
      }
    };
    loadInventoryData();
  }, []);

  const handleQuantityChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    list: Inventory,
    size: "20L" | "30L" | "50L"
  ) => {
    const value = e.target.value;
    if (/^\d*\.?\d*$/.test(value)) {
      const newValue = value === "" ? 0 : Number(value);
      setTempQuantities((prev) => ({
        ...prev,
        [list.id]: {
          ...prev[list.id],
          [`quantity_${size}`]: newValue,
        },
      }));
      setBulkUpdatePayload((prev) => {
        const existingItem = prev.find(
          (entry) =>
            entry.productId === list.productId &&
            entry.inventoryId === list.inventoryId
        );

        if (existingItem) {
          return prev.map((entry) =>
            entry.productId === list.productId &&
            entry.inventoryId === list.inventoryId
              ? { ...entry, [`quantity_${size}`]: newValue }
              : entry
          );
        } else {
          return [
            ...prev,
            {
              productId: list.productId,
              inventoryId: list.inventoryId,
              quantity_20L: list.quantity_20L ?? 0,
              quantity_30L: list.quantity_30L ?? 0,
              quantity_50L: list.quantity_50L ?? 0,
              [`quantity_${size}`]: newValue,
            },
          ];
        }
      });
    }
  };

  const handleSave = async () => {
    setError(false);
    try {
      const response = await api.put(
        `${productInventoryEndPoints.productInventory}`,
        bulkUpdatePayload
      );
      if (response?.data) {
        setInventoryDataState((prevData) =>
          prevData.map((item) =>
            tempQuantities[item.id]
              ? { ...item, ...tempQuantities[item.id] }
              : item
          )
        );
        setTempQuantities({});
        setToastMessage("Inventory updated successfully");
        setInventoryDataState((prev) =>
          prev.map((item) =>
            tempQuantities[item.id]
              ? { ...item, ...tempQuantities[item.id] }
              : item
          )
        );
        setBulkUpdatePayload([]);
        setTempQuantities({});
        setIsEditing(null);
      } else {
        console.error("Error updating inventory:", error);
        setError(true);
        setToastMessage("Failed to update inventory");
      }
    } catch (error) {
      console.error("Error updating inventory:", error);
      setError(true);
      setToastMessage("Failed to update inventory");
    }
    setShowToast(true);
  };

  const commonLabelStyle: SxProps = {
    color: "black",
    fontWeight: 400,
  };

  const columns: Column[] = [
    {
      label: "List",
      field: "id",
      align: "left",
      cellStyle: { width: "70px", fontWeight: "500" },
      labelStyle: commonLabelStyle,
    },
    {
      label: "Product Name",
      field: "productName",
      align: "left",
      cellStyle: { width: "90px", fontWeight: "500" },
      labelStyle: commonLabelStyle,
    },
    {
      label: "Product Id",
      field: "productId",
      align: "center",
      cellStyle: { width: "100px", textAlign: "center" },
      labelStyle: commonLabelStyle,
    },
    {
      label: "20L",
      field: "quantity_20L",
      align: "center",
      cellStyle: { width: "90px", textAlign: "center" },
      labelStyle: commonLabelStyle,
    },
    {
      label: "30L",
      field: "quantity_30L",
      align: "center",
      cellStyle: { width: "90px", textAlign: "center" },
      labelStyle: commonLabelStyle,
    },
    {
      label: "50L",
      field: "quantity_50L",
      align: "center",
      cellStyle: { width: "90px", textAlign: "center" },
      labelStyle: commonLabelStyle,
    },
    {
      label: "Edit",
      field: "editIcon",
      align: "center",
      cellStyle: { width: "65px", textAlign: "center" },
      labelStyle: commonLabelStyle,
    },
  ];

  const inventoryTableData = useMemo(() => {
    return inventoryDataState.map((list) => ({
      id: list.id,
      productName: list.productName,
      productId: list.productId,
      quantity_20L:
        isEditing === list.id ? (
          <TextField
            value={
              tempQuantities[list.id]?.[`quantity_20L`] !== undefined
                ? tempQuantities[list.id][`quantity_20L`]
                : list[`quantity_20L`]
            }
            onChange={(e) => handleQuantityChange(e, list, "20L")}
            size="small"
            variant="outlined"
          />
        ) : (
          list.quantity_20L
        ),
      quantity_30L:
        isEditing === list.id ? (
          <TextField
            value={
              tempQuantities[list.id]?.[`quantity_30L`] !== undefined
                ? tempQuantities[list.id][`quantity_30L`]
                : list[`quantity_30L`]
            }
            onChange={(e) => handleQuantityChange(e, list, "30L")}
            size="small"
            variant="outlined"
          />
        ) : (
          list.quantity_30L
        ),
      quantity_50L:
        isEditing === list.id ? (
          <TextField
            value={
              tempQuantities[list.id]?.[`quantity_50L`] !== undefined
                ? tempQuantities[list.id][`quantity_50L`]
                : list[`quantity_50L`]
            }
            onChange={(e) => handleQuantityChange(e, list, "50L")}
            size="small"
            variant="outlined"
          />
        ) : (
          list.quantity_50L
        ),
      editIcon: (
        <IconButton
          data-testid={`edit-button-${list.id}`}
          onClick={() => {
            if (isEditing !== null) {
              // Save the changes of the previously edited product
              setInventoryDataState((prevData) =>
                prevData.map((item) =>
                  item.id === isEditing
                    ? { ...item, ...tempQuantities[isEditing] }
                    : item
                )
              );

              setBulkUpdatePayload((prev) => [
                ...prev.filter(
                  (entry) =>
                    entry.productId !==
                    inventoryDataState.find((item) => item.id === isEditing)
                      ?.productId
                ),
                {
                  ...tempQuantities[isEditing],
                  productId: inventoryDataState.find(
                    (item) => item.id === isEditing
                  )!.productId,
                  inventoryId: inventoryDataState.find(
                    (item) => item.id === isEditing
                  )!.inventoryId,
                },
              ]);
            }

            setIsEditing(list.id);

            setTempQuantities((prev) => ({
              ...prev,
              [list.id]: {
                quantity_20L: prev[list.id]?.quantity_20L ?? list.quantity_20L,
                quantity_30L: prev[list.id]?.quantity_30L ?? list.quantity_30L,
                quantity_50L: prev[list.id]?.quantity_50L ?? list.quantity_50L,
              },
            }));
          }}
        >
          <img src={editIcon} alt="Edit" />
        </IconButton>
      ),
    }));
  }, [inventoryDataState, isEditing, tempQuantities]);

  return (
    <Card sx={{ boxShadow: 0, paddingTop: 2, padding: "1rem" }}>
      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={error}
      />
      <DataTable
        alternateBg
        columns={columns}
        data={inventoryTableData}
        emptyStateMessage="No Products available in inventory"
      />
      {isEditing !== null && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            margin: "40px",
          }}
        >
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              fontSize: "14px",
              width: "248px",
              height: "48px",
              borderRadius: "16px",
              padding: "10px",
            }}
          >
            Save
          </Button>
        </div>
      )}
    </Card>
  );
};

export default InventoryDetails;
